/**
 * Top-level App — composes providers, renders the shell (TopBar +
 * MobileDrawer + Footer), and routes the current `view` enum to the right
 * view component.
 *
 * View state lifts to `App` (above AppProviders) per ADR-0018 §7 so the
 * AuthProvider's bootstrap-401 routing + the HTTP client's
 * `on_unauthorized` callback can both call `setView('login')` via the
 * providers' `setView` prop. The dashboard's bootstrap auth check
 * gates view rendering on the AuthProvider's resolved state — a
 * loading splash blocks the first paint until the cookie-auto-attach
 * round-trip completes.
 *
 * Mirrors `Foyer_Dashboard_CURRENT.jsx` line 10004; typed and stripped to
 * Phase 1 scope.
 */

import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';

import { FontLoader } from '@sill/ui';

import { AppProviders } from './app/providers.js';
import { useAuth } from './app/auth-context.js';
import { initialView, RESTORABLE_VIEWS } from './app/initial-view.js';
import { consumeReturnPath } from './lib/return-path.js';
const RESTORABLE_SET: ReadonlySet<string> = new Set(RESTORABLE_VIEWS);
import type { SetView, View, ViewOptions } from './app/view-router.js';
import { ViewRouter } from './app/view-router.js';
import { ActingBanner } from './components/ActingBanner.js';
import { DeletionGraceBanner } from './components/shell/DeletionGraceBanner.js';
import { SupportSessionBanner } from './components/SupportSessionBanner.js';
import { Footer } from './components/shell/Footer.js';
import { MobileDrawer } from './components/shell/MobileDrawer.js';
import { TopBar } from './components/shell/TopBar.js';
import { Tooltip } from './components/atoms/Tooltip.js';
import { useC } from './lib/theme.js';

const sansFont = "'Geist', sans-serif";

interface AppInnerProps {
  view: View;
  setView: SetView;
}

function AppInner({ view, setView }: AppInnerProps): JSX.Element {
  const C = useC();
  const auth = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // If the bootstrap auth check takes more than 20 s, the machine is likely
  // still cold-starting (or the network is unreliable). Show a retry prompt
  // so the user doesn't see an indefinite spinner and reach for "clear cache".
  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false);
  useEffect(() => {
    if (auth.state.kind !== 'bootstrap') return;
    const t = setTimeout(() => setBootstrapTimedOut(true), 20_000);
    return () => clearTimeout(t);
  }, [auth.state.kind]);

  // Reflect the view on the URL for back/forward navigation. Only for
  // restorable views — auth-flow views (login/verify/welcome) must NOT
  // be written to the URL. `setView` already deletes `?view=` for
  // non-restorable views; this effect runs after the state change and
  // would otherwise re-add e.g. `?view=login`, defeating that cleanup.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!RESTORABLE_SET.has(view)) return;
    const u = new URL(window.location.href);
    if (u.searchParams.get('view') !== view) {
      u.searchParams.set('view', view);
      window.history.replaceState({}, '', u.toString());
    }
  }, [view]);

  // ADR-0261 — consume a saved `/connect/:request_id` return path on
  // the first authenticated bootstrap. A signed-out visitor to the
  // consent landing saved the path to localStorage before being
  // shown Login; after the magic-link round trip the browser lands
  // on `/`, so this effect replays the URL and sets the view to
  // `connectApproval` where the preview can resume. One-shot: the
  // consume helper deletes the storage slot before we read it.
  useEffect(() => {
    if (auth.state.kind !== 'authenticated') return;
    const next = consumeReturnPath();
    if (next === null) return;
    if (typeof window === 'undefined') return;
    try {
      window.history.replaceState({}, '', next);
    } catch {
      /* enhancement only */
    }
    setView('connectApproval');
    // Only fires when the auth state transitions to `authenticated`.
    // `setView` is referentially stable from App; we exclude it from
    // the deps so a parent re-render cannot replay the consume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.state.kind]);

  // ADR-0018 §7.2 — bootstrap auth check renders a splash until the
  // AuthProvider resolves. The /verify path is the exception: it must
  // render its top-level-redirect bridge regardless of the auth state
  // (the visitor lands here BEFORE the cookie is set).
  // The /welcome confirm page joins /verify as a pre-cookie surface
  // (ADR-0185 follow-up): both must render during bootstrap.
  // A stalled bootstrap (every attempt timed out / network error) keeps the
  // splash with the details and a retry — never the sign-in screen, which
  // read as "the magic link failed" (founder 2026-09-23).
  if ((auth.state.kind === 'bootstrap' || auth.state.kind === 'service_error') && view !== 'verify' && view !== 'welcome' && view !== 'login' && view !== 'agencyAccessApproval' && view !== 'connectApproval') {
    const stalled = auth.state.kind === 'service_error';
    const diagnostic = auth.state.kind === 'service_error' ? auth.state.diagnostic : undefined;
    return (
      <div
        style={{
          background: C.bg,
          minHeight: '100vh',
          fontFamily: sansFont,
          color: C.muted,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          fontSize: 13,
          letterSpacing: 1.5,
        }}
      >
        {bootstrapTimedOut || stalled ? (
          <>
            <span style={{ color: C.muted }}>{stalled ? 'Sill could not be reached from this browser.' : 'Taking longer than expected.'}</span>
            {stalled ? (
              <span style={{ color: C.dim, fontSize: 12, letterSpacing: 0, maxWidth: 420, textAlign: 'center', lineHeight: 1.5 }}>
                You are still signed in. Retry, or send the details below to support.
              </span>
            ) : null}
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.text,
                fontFamily: sansFont,
                fontSize: 12,
                letterSpacing: 1,
                padding: '6px 16px',
                cursor: 'pointer',
                borderRadius: 2,
              }}
            >
              Retry
            </button>
            {diagnostic !== undefined ? (
              <Tooltip content="Click to copy" displayMode="block">
              <pre
                data-testid="bootstrap-diagnostic"
                onClick={() => { try { void navigator.clipboard.writeText(diagnostic); } catch { /* ignore */ } }}
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: 0, color: C.dim, whiteSpace: 'pre-wrap', maxWidth: 520, textAlign: 'left', cursor: 'copy', margin: 0, padding: '8px 12px', border: `1px dashed ${C.border}`, borderRadius: 2 }}
              >
                {diagnostic}
              </pre>
              </Tooltip>
            ) : null}
          </>
        ) : (
          <span>Loading…</span>
        )}
      </div>
    );
  }

  const showShell =
    view !== 'login'
    && view !== 'verify'
    && view !== 'welcome'
    && view !== 'addSite'
    && view !== 'agencyAccessApproval'
    && view !== 'connectApproval';

  return (
    <div
      style={{
        background: C.bg,
        minHeight: '100vh',
        fontFamily: sansFont,
        color: C.text,
      }}
    >
      {showShell ? (
        <TopBar view={view} setView={setView} onMobileMenu={() => setMobileNavOpen(true)} />
      ) : null}
      {showShell ? <DeletionGraceBanner /> : null}
      {/* ADR-0216 — always render the support-session banner when the
          resolved session is a staff support one. Rendered ABOVE the
          view so it stays visible on every tab; the component reads
          user.support_session and no-ops on merchant sessions. */}
      {showShell ? <SupportSessionBanner /> : null}
      {/* ADR-0237 — acting banner for agency sessions. Same slot as
          the support-session banner; the two are mutually exclusive
          by design (a support session is a staff auth path, an acting
          session is an agency auth path). */}
      {showShell ? <ActingBanner /> : null}
      <AnimatePresence>
        {mobileNavOpen ? (
          <MobileDrawer
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            view={view}
            setView={setView}
          />
        ) : null}
      </AnimatePresence>
      <main>
        <ViewRouter view={view} setView={setView} />
      </main>
      {showShell ? <Footer /> : null}
    </div>
  );
}

export function App(): JSX.Element {
  // View state lives at App so AppProviders + AppInner share the same
  // setView. The AuthProvider needs setView to route to login on 401;
  // AppInner needs view + setView to drive ViewRouter + TopBar.
  const [view, setViewState] = useState<View>(() => initialView());

  // Browser back/forward walks the in-app views (founder 2026-09-23: back
  // exited the app). `?view=` is written with pushState when the view
  // changes and popstate re-resolves it through `initialView`, the same
  // allow-listed reader a fresh load uses.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = (): void => { setViewState(initialView()); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const setView: SetView = (v: View, opts?: ViewOptions): void => {
    const changed = v !== view;
    setViewState(v);
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
      // Persist the active view so refresh + deep links keep the tab.
      // pushState when the view changes so Back returns to the previous
      // tab; replaceState for same-view option updates. Auth-flow views
      // never land in the URL. The one-shot `escalation` / `record` /
      // `q` deep-link params drop on navigation away.
      try {
        const url = new URL(window.location.href);
        if ((RESTORABLE_VIEWS as readonly string[]).includes(v)) {
          url.searchParams.set('view', v);
        } else {
          url.searchParams.delete('view');
        }
        // Deep-link `?escalation=` one-shot — set when a caller navigates
        // to a specific held mandate (dashboard PendingResolutions rows;
        // the escalation-notify email CTA writes the same param directly).
        // Cleared when navigating away from the Resolution view.
        if (v === 'resolution' && opts?.escalation !== undefined && opts.escalation.length > 0) {
          url.searchParams.set('escalation', opts.escalation);
        } else {
          // No id (e.g. "Review all →") or navigating away — drop any
          // stale selection so the queue opens at the top row.
          url.searchParams.delete('escalation');
        }
        // Deep-link params — written when the caller supplies them,
        // cleared otherwise. The destination view consumes them on
        // mount (Mandates reads `?record=` + `?q=`, mirroring the
        // existing `?escalation=` one-shot on Resolution).
        if (v === 'mandates' && opts?.record !== undefined && opts.record.length > 0) {
          url.searchParams.set('record', opts.record);
        } else {
          url.searchParams.delete('record');
        }
        if (v === 'agents' && opts?.agent_kind !== undefined) {
          url.searchParams.set('agent_kind', opts.agent_kind);
        } else if (v !== 'agents') {
          url.searchParams.delete('agent_kind');
        }
        if (v === 'mandates' && opts?.q !== undefined && opts.q.length > 0) {
          url.searchParams.set('q', opts.q);
        } else if (v !== 'mandates') {
          url.searchParams.delete('q');
        }
        // Visits chip one-shot (`?segment=`, e.g. People for human rows).
        if (v === 'mandates' && opts?.segment !== undefined) {
          url.searchParams.set('segment', opts.segment);
        } else {
          url.searchParams.delete('segment');
        }
        // Guardrails pre-fill — written by BotClassificationRow's "Add rule" CTA,
        // cleared by Guardrails on mount after reading once.
        if (v === 'guardrails' && opts?.preset_rule_type !== undefined && opts.preset_rule_type.length > 0) {
          url.searchParams.set('preset_rule_type', opts.preset_rule_type);
          if (opts.preset_agent_id !== undefined && opts.preset_agent_id.length > 0) {
            url.searchParams.set('preset_agent_id', opts.preset_agent_id);
          } else {
            url.searchParams.delete('preset_agent_id');
          }
          if (opts.preset_action_on_match !== undefined) {
            url.searchParams.set('preset_action_on_match', opts.preset_action_on_match);
          } else {
            url.searchParams.delete('preset_action_on_match');
          }
        } else {
          url.searchParams.delete('preset_rule_type');
          url.searchParams.delete('preset_agent_id');
          url.searchParams.delete('preset_action_on_match');
        }
        // Settings deep-link — writes `settings_tab` + optional
        // `settings_section`. Settings consumes `settings_tab` on mount
        // and clears it (existing behavior); the section carries a
        // stable id on the target block that Settings scrolls into
        // view + briefly outlines.
        if (v === 'settings' && opts?.settings_tab !== undefined) {
          url.searchParams.set('settings_tab', opts.settings_tab);
        } else if (v === 'settings') {
          // Leave an existing settings_tab if present (OAuth returns
          // may already have set it); only clear when a different view.
        } else {
          url.searchParams.delete('settings_tab');
        }
        if (v === 'settings' && opts?.settings_section !== undefined && opts.settings_section.length > 0) {
          url.searchParams.set('settings_section', opts.settings_section);
        } else if (v !== 'settings') {
          url.searchParams.delete('settings_section');
        }
        // Agents view — pre-select an agent key + optional detail tab.
        if (v === 'agents' && opts?.agent !== undefined && opts.agent.length > 0) {
          url.searchParams.set('agent', opts.agent);
        } else if (v !== 'agents') {
          url.searchParams.delete('agent');
        }
        if (v === 'agents' && opts?.agents_mode !== undefined) {
          url.searchParams.set('agents_mode', opts.agents_mode);
        } else if (v !== 'agents') {
          url.searchParams.delete('agents_mode');
        }
        // Security view — pre-select a sub-tab. Also carries `?threat=`
        // through so a threat alert email can link straight to an open
        // sender inside Security · Attackers.
        if (v === 'security' && opts?.security_section !== undefined) {
          url.searchParams.set('section', opts.security_section);
        } else if (v !== 'security') {
          url.searchParams.delete('section');
        }
        if (v === 'security' && opts?.threat_alert_id !== undefined && opts.threat_alert_id.length > 0) {
          url.searchParams.set('threat', opts.threat_alert_id);
        } else if (v !== 'security' && v !== 'mandates') {
          url.searchParams.delete('threat');
        }
        if (changed && (RESTORABLE_VIEWS as readonly string[]).includes(v)) {
          window.history.pushState(null, '', url.toString());
        } else {
          window.history.replaceState(null, '', url.toString());
        }
      } catch {
        // URL persistence is enhancement only.
      }
    }
  };

  return (
    <AppProviders setView={setView}>
      <FontLoader />
      <AppInner view={view} setView={setView} />
    </AppProviders>
  );
}
